import { Component, Output, EventEmitter, Input, inject, signal, computed, effect, ElementRef, ViewChild, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AvatarModule } from 'primeng/avatar';
import { AuthService } from '../auth/auth.service';
import { ThemeService } from '../theme/theme.service';
import { NAV_GROUPS, NavGroup, NavChild } from './nav-config';
import { environment } from '../../../environments/environment';

interface SearchResult {
  group: NavGroup;
  child: NavChild;
}

interface TipoCambio {
  compra: number;
  venta: number;
}

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, AvatarModule],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.css',
})
export class TopbarComponent implements OnInit {
  @Input() isHub = false;
  @Input() sidebarCollapsed = false;
  @Output() menuToggle = new EventEmitter<void>();
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  auth = inject(AuthService);
  theme = inject(ThemeService);
  private router = inject(Router);
  private http = inject(HttpClient);

  menuOpen = false;
  searchActive = signal(false);
  searchQuery = signal('');
  selectedIndex = signal(0);
  tipoCambio = signal<TipoCambio | null>(null);

  ngOnInit(): void {
    this.fetchTipoCambio();
  }

  private fetchTipoCambio(): void {
    const today = new Date();
    const fecha = today.getFullYear() + '-' +
      String(today.getMonth() + 1).padStart(2, '0') + '-' +
      String(today.getDate()).padStart(2, '0');

    const headers = new HttpHeaders({
      Authorization: `Bearer ${environment.documentLookupToken}`,
    });

    this.http.post<any>(
      `${environment.documentLookupUrl}/tipo-de-cambio`,
      { fecha },
      { headers }
    ).subscribe({
      next: (res) => {
        const data = res?.data || res;
        if (data?.compra && data?.venta) {
          this.tipoCambio.set({
            compra: parseFloat(data.compra),
            venta: parseFloat(data.venta),
          });
        }
      },
      error: () => {},
    });
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault();
      this.searchInputRef?.nativeElement?.focus();
      this.searchActive.set(true);
    }
  }

  searchResults = computed<SearchResult[]>(() => {
    const q = this.normalize(this.searchQuery().trim());
    if (!q) return [];
    const results: SearchResult[] = [];
    for (const group of NAV_GROUPS) {
      for (const child of group.children) {
        if (!child.module || this.auth.hasModule(child.module)) {
          if (
            this.normalize(child.label).includes(q) ||
            this.normalize(group.label).includes(q)
          ) {
            results.push({ group, child });
          }
        }
      }
      if (results.length >= 6) break;
    }
    return results.slice(0, 6);
  });

  private resetIndex = effect(() => {
    this.searchResults();
    this.selectedIndex.set(0);
  });

  get userInitials(): string {
    const user = this.auth.user();
    if (!user) return '?';
    const first = user.user_firstname?.[0] || user.username[0] || '';
    const last = user.user_lastname?.[0] || '';
    return (first + last).toUpperCase();
  }

  private normalize(text: string): string {
    return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  highlightMatch(text: string): string {
    const q = this.searchQuery().trim();
    if (!q) return text;
    const regex = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<span style="color:#006d3c;font-weight:700">$1</span>');
  }

  moveSelection(delta: number, event: Event): void {
    event.preventDefault();
    const len = this.searchResults().length;
    if (!len) return;
    this.selectedIndex.update(i => {
      const next = i + delta;
      if (next < 0) return len - 1;
      if (next >= len) return 0;
      return next;
    });
  }

  navigateSelected(): void {
    const results = this.searchResults();
    if (results.length > 0) {
      const idx = Math.min(this.selectedIndex(), results.length - 1);
      this.router.navigate([results[idx].child.route]);
      this.closeSearch();
    }
  }

  closeSearch(): void {
    this.searchActive.set(false);
    this.searchQuery.set('');
    this.selectedIndex.set(0);
  }

  clearSearch(): void {
    this.searchQuery.set('');
  }

  goToProfile(): void {
    this.menuOpen = false;
    this.router.navigate(['/profile']);
  }

  logout(): void {
    this.menuOpen = false;
    this.auth.logout();
  }
}
